import { UrlSuggestions } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/SearchBar/UrlSuggestions';
import styled from '@emotion/styled';
import { SearchInput, tokens } from '@hotjar/hotsauce';
import type { TopPage } from '@hotjar/heatmaps';
import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { noop } from '@hotjar/webapp/lib/function';

interface AddOn {
  element: JSX.Element;
  isVisible: boolean;
  name: string;
  width: keyof typeof tokens.spacer;
}

interface UrlSearchProps {
  addOns?: AddOn[];
  className?: string;
  initiallyOpen?: boolean;
  onSuggestionSelect?: (selectedUrl: string, index: number) => void;
  onUrlChange: (url: string) => void;
  placeholderMessage: string;
  shouldHideUrlSuggestions: boolean;
  sourceUrl?: string;
  urlSuggestions?: TopPage[];
}

const SearchInputContainer = styled.div`
  position: relative;
`;

// Override the input to create space for the icon
// and avoid overlapping the text.
const StyledSearchInput = styled(SearchInput)<{ rightPadding?: number }>`
  input {
    padding-right: ${({ rightPadding }) => (rightPadding ? `${rightPadding}px` : 0)};
  }
`;

const InputStyles = css`
  display: flex;
  flex: 1;

  &:focus {
    box-shadow: none;
  }
`;

const SUGGESTIONS_LIMIT = 10;

export const UrlSearch = ({
  addOns = [],
  className,
  initiallyOpen = false,
  onSuggestionSelect = noop,
  onUrlChange,
  placeholderMessage,
  shouldHideUrlSuggestions,
  sourceUrl = '',
  urlSuggestions = [],
}: UrlSearchProps): JSX.Element => {
  const [showSuggestions, setShowSuggestions] = useState(initiallyOpen || false);

  const handleBlurEvent = () => {
    setShowSuggestions(false);
  };
  const handleFocusEvent = () => {
    setShowSuggestions(true);
  };

  // Here we use `useState` instead of `useRef` because we want to catch input remounts
  // and trigger some useEffects again (eg. in case when the input gets mounted again
  // we want to focus to it in case `initiallyOpen` is set to `true`).
  const [input, setInput] = useState<HTMLInputElement | null>(null);
  // Here we use `useCallback` instead of `useRef`, because we want to clean up and re-set the
  // input event listeners when the search input gets unmounted and remounted.
  // We weren't able to catch such ref changes by using useRef combined with a useEffect so the
  // the event listeners used to stop working after input was remounted (eg. due to a change to
  // targeting selection).
  // More info:
  // https://medium.com/welldone-software/usecallback-might-be-what-you-meant-by-useref-useeffect-773bc0278ae
  // https://medium.com/@teh_builder/ref-objects-inside-useeffect-hooks-eb7c15198780
  const setRef = useCallback(
    (node) => {
      if (input) {
        // clean up events or references added to the last instance
        input.removeEventListener('blur', handleBlurEvent);
        input.removeEventListener('focus', handleFocusEvent);
      }

      if (node) {
        // add event listeners
        node.addEventListener('blur', handleBlurEvent);
        node.addEventListener('focus', handleFocusEvent);
      }

      setInput(node);
    },
    [input],
  );

  const [url, setUrl] = useState(sourceUrl);

  const getSlicedListOfSuggestions = (list?: TopPage[], itemCount = SUGGESTIONS_LIMIT) =>
    (list || []).slice(0, itemCount);

  const filterSuggestions = (term = '') =>
    getSlicedListOfSuggestions(
      urlSuggestions.filter((suggestion) => suggestion.pageUrl.includes(term)),
    );
  const suggestions = filterSuggestions(url);

  const handleSuggestionSelect = (selectedUrl: string, index: number) => {
    setUrl(selectedUrl);
    onSuggestionSelect(selectedUrl, index);
    // Trigger Blur to remove focus and close autocomplete
    input?.blur?.();
    setShowSuggestions(false);
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setUrl(value);
    onUrlChange(value);
  };

  const calculateInputPadding = () => {
    return addOns.reduce((acc, addOn) => acc + parseInt(tokens.spacer[addOn.width], 10), 0);
  };

  useEffect(() => {
    setUrl(sourceUrl);
  }, [sourceUrl]);

  useEffect(() => {
    if (initiallyOpen) {
      input?.focus?.();
    }
  }, [initiallyOpen, input]);

  useEffect(() => {
    if (suggestions.length === 0) {
      setShowSuggestions(false);
    } else {
      setShowSuggestions(Boolean(input?.matches(':focus')));
    }
  }, [suggestions, input]);

  return (
    <UrlSuggestions
      className={className}
      url={url}
      suggestions={suggestions}
      onSelect={handleSuggestionSelect}
      hide={shouldHideUrlSuggestions}
      showSuggestions={showSuggestions}
    >
      <SearchInputContainer>
        <StyledSearchInput
          value={url}
          ref={setRef}
          onChange={handleUrlChange}
          placeholder={placeholderMessage}
          inputStyles={InputStyles}
          rightPadding={calculateInputPadding()}
        />
        {addOns.map((addOn) => addOn.isVisible && <span key={addOn.name}>{addOn.element}</span>)}
      </SearchInputContainer>
    </UrlSuggestions>
  );
};
